import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { settingsService } from 'v2/shared/services/settings-service'
import { getAPIConsoleQueryKeys, getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { ConvChannels } from 'v2/types/api/conversations'
import type {
  InitiateCallerIdVerificationPayload,
  RPhoneNumbersPaginated,
} from 'v2/types/api/settings'
import { accountNumbersQueryParamsMap } from '../phone-numbers/queries'
import type { InternalNumberField } from './internal-number-sheet'

// Subaccounts Query (via Plivo API Proxy)
export const useSubaccountsQuery = (enabled: boolean = true) => {
  return useQuery({
    queryKey: getAPIConsoleQueryKeys().subaccounts,
    queryFn: async () => {
      const response = await settingsService.listSubaccountsInternal()
      return response.data
    },
    enabled,
  })
}

// An important query that fetches account numbers
// Used in multiple places - PhoneNumbers, WhatsappNumbers, SenderIDs
export const useAccountNumbersQuery = (
  type: keyof typeof accountNumbersQueryParamsMap,
  channel?: ConvChannels,
  // Temporary flag to control query execution when coming from channel page
  enabled = true,
) => {
  const params = channel
    ? accountNumbersQueryParamsMap[type](channel)
    : accountNumbersQueryParamsMap.allChannels()
  return useQuery({
    queryKey: getSettingsQueryKeys().accountNumbers(params),
    queryFn: async () => {
      const response = await settingsService.getAccountNumbers(params)
      return response.data
    },
    enabled,
  })
}

export const useBuyNumberMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (number: string) => settingsService.buyNumber(number),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().accountNumbersBase })
    },
  })
}

export const useDeleteNumberMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (number: string) => settingsService.deleteNumber(number.split('+')[1]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().accountNumbersBase })
    },
  })
}

export const useAddInternalNumberMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: InternalNumberField) => {
      const response = await settingsService.addInternalNumber(payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().internalNumbers })
    },
  })
}
export const useDeleteInternalNumberMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (contact_id: number) => {
      const response = await settingsService.deleteInternalNumber(contact_id)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().internalNumbers })
    },
  })
}

export const useUpdateInternalNumberMutation = (contact_id: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: InternalNumberField) => {
      const response = await settingsService.updateInternalNumber(contact_id, payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().internalNumbers })
    },
  })
}

export const useUpdateSenderIdMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: string; alias: string }) =>
      settingsService.updateSenderId(data.id, { name_tag: data.alias }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getSettingsQueryKeys().accountNumbers(accountNumbersQueryParamsMap.senderId()),
      })
    },
  })
}

export const useDeleteSenderIdMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (senderId: string) => settingsService.deleteSenderId(senderId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getSettingsQueryKeys().accountNumbers(accountNumbersQueryParamsMap.senderId()),
      })
    },
  })
}

export const useVerifiedCallerIdsQuery = () => {
  return useQuery({
    queryKey: getSettingsQueryKeys().verifiedCallerIds,
    queryFn: async () => {
      const response = await settingsService.getVerifiedCallerIds()
      return response.data
    },
    select: (data) => data.objects || [],
  })
}

export const useVerifiedCallerIdQuery = (number: string | undefined) => {
  return useQuery({
    queryKey: [...getSettingsQueryKeys().verifiedCallerIds, number],
    queryFn: async () => {
      if (!number) {
        return null
      }
      const response = await settingsService.getVerifiedCallerId(number)
      return response.data
    },
    enabled: !!number,
  })
}

export const useUpdateVerifiedCallerIdMutation = (number: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { alias: string; subaccount?: string }) =>
      settingsService.updateVerifiedCallerId(number, payload),
    onSuccess: async (data) => {
      // Update the specific caller ID in cache directly
      queryClient.setQueryData([...getSettingsQueryKeys().verifiedCallerIds, number], data.data)
      await new Promise((resolve) => setTimeout(resolve, 500))
      await queryClient.invalidateQueries({
        queryKey: getSettingsQueryKeys().verifiedCallerIds,
      })
    },
  })
}

export const useDeleteVerifiedCallerIdMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (number: string) => settingsService.deleteVerifiedCallerId(number),
    onSuccess: async () => {
      await new Promise((resolve) => setTimeout(resolve, 500))
      await queryClient.invalidateQueries({
        queryKey: getSettingsQueryKeys().verifiedCallerIds,
        exact: true,
      })
    },
  })
}

// New paginated phone numbers query
export const phoneNumbersQueryConfig = () => ({
  queryKey: getSettingsQueryKeys().phoneNumbers,
  fetchService: (queryParams: Record<string, unknown>) =>
    settingsService.getPhoneNumbers(queryParams),
  extractData: (data: RPhoneNumbersPaginated) => ({
    data: data.numbers,
    totalCount: data.total,
  }),
})

export const usePhoneNumbersQuery = (params: Record<string, unknown>) =>
  useQuery({
    queryKey: [...getSettingsQueryKeys().phoneNumbers, params],
    queryFn: () => phoneNumbersQueryConfig().fetchService(params),
  })

// Plivo verification mutations
export const usePlivoInitiateVerificationMutation = () => {
  return useMutation({
    mutationFn: async (payload: InitiateCallerIdVerificationPayload) => {
      const response = await settingsService.initiatePlivoVerification(payload)
      return response.data
    },
  })
}

export const usePlivoVerifyCallerIdMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ verification_uuid, otp }: { verification_uuid: string; otp: string }) => {
      const response = await settingsService.verifyPlivoCallerId(verification_uuid, { otp })
      return response.data
    },
    onSuccess: async () => {
      await new Promise((resolve) => setTimeout(resolve, 500))
      await queryClient.invalidateQueries({
        queryKey: getSettingsQueryKeys().verifiedCallerIds,
      })
    },
  })
}
